import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ProductImport } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<ProductImportListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<APIList<ProductImport>, Error>,
    'queryKey' | 'queryFn'
  >;
  enabled?: boolean
}

interface ProductImportListGetParams {
  limit: number;
  offset: number;
  sort: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
}

export const useProductImportListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<ProductImportListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<APIList<ProductImport>, Error>(
    [Entities.ProductImport, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
      } = queryParams
      const { data } = await api.v1AdminProductsImportHistoryGet(
        limit,
        offset,
        sort,
      )
      return data as APIList<ProductImport>
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
